<template>
  <main class="dashboard navigable">
    <article
      v-if="account && (canInstallPWA || canInstallIOS)"
      class="dash-item app-banner"
    >
      <div class="title level">
        <div class="level-left">
          {{ $configurations.installAppText }}
        </div>
        <div class="level-right">
          <b-button
            class="level-item"
            type="is-primary"
            @click="installApp"
          >
            Install
          </b-button>
        </div>
      </div>
    </article>
    <article
      v-else-if="showCustomHomeBanner"
      class="dash-item app-banner"
      @click="handleBannerClick"
    >
      <ignite-markdown>
        {{ customHomeBanner.markdown }}
      </ignite-markdown>
    </article>
    <article
      v-else-if="account && (!isSubscribed && browserNotification)"
      class="dash-item app-banner"
    >
      <div class="title level">
        <div class="level-left">
          {{ notificationBannerText }}
        </div>
        <div class="level-right">
          <b-button
            class="level-item"
            type="is-primary"
            @click="requestNotification"
          >
            Yes
          </b-button>
        </div>
      </div>
    </article>

    <Products id="products" />
    <Renewals id="renewals" />
    <Policies id="policies" />
    <Quotes id="quotes" />
    <Benefits
      v-if="hasBenefits"
      id="benefits"
    />
    <Claims id="claims" />
    <Documents id="documents" />

    <IOSPWAPrompt :active.sync="iosPromptActive" />
    <PasswordChange
      :active.sync="passwordChangeShown"
      :can-cancel="false"
      @complete="passwordChangeShown = false"
    />
  </main>
</template>

<script lang="ts">
import type { ICustomBanner } from '@/configurations/config-objects/ICustomBanner'
import type Account from '@/services/models/Account'
import type PolicySummary from '@/services/values/PolicySummary'
import { Component, Vue } from 'vue-property-decorator'
import { getAuthServiceConfig } from '@/auth-service'
import IgniteMarkdown from '@/components/controls/IgniteMarkdown.vue'
import Benefits from '@/components/dashboard/Benefits.vue'
import Claims from '@/components/dashboard/Claims.vue'
import Documents from '@/components/dashboard/Documents.vue'
import IOSPWAPrompt from '@/components/dashboard/IOSPWAPrompt.vue'
import Policies from '@/components/dashboard/Policies.vue'
import Products from '@/components/dashboard/Products.vue'
import Quotes from '@/components/dashboard/Quotes.vue'
import Renewals from '@/components/dashboard/Renewals.vue'
import PasswordChange from '@/components/login/PasswordChange.vue'
import useAccountStore from '@/stores/account-store'
import usePoliciesStore from '@/stores/policies-store'
import WebPlatformEnum from '../services/enums/WebPlatformEnum'
import NotAuthorisedError from '../services/error/NotAuthorisedError'

@Component({
  components: {
    Policies,
    Renewals,
    Quotes,
    Claims,
    Documents,
    IOSPWAPrompt,
    PasswordChange,
    Benefits,
    Products,
    IgniteMarkdown,
  },
})
export default class Home extends Vue {
  iosPromptActive = false

  subscribeClicked = false

  passwordChangeShown = false

  policies: PolicySummary[] | undefined = []

  async mounted(): Promise<void> {
    const accountStore = useAccountStore()

    try {
      const account = await this.$services.accountService.getCurrent()
      accountStore.setAccount(account)

      const authServiceConfig = getAuthServiceConfig()

      if (authServiceConfig.enabled) {
        if (authServiceConfig.type === 'auth-prospect')
          this.passwordChangeShown = true
      }
      else if (!account.hasCustomerCredentials) {
        this.passwordChangeShown = true
      }
    }
    catch (error) {
      if (!(error instanceof NotAuthorisedError))
        throw error
    }

    await usePoliciesStore().retrievePolicies(this.$services, true)
    this.policies = usePoliciesStore().policies
  }

  get isSubscribed(): boolean {
    return this.$pwa.isSubscribed || this.subscribeClicked
  }

  get hasBenefits(): boolean {
    return this.$configurations.benefitsDashboardItem.visible ?? false
  }

  get account(): Account | null {
    const accountStore = useAccountStore()
    return accountStore.account
  }

  installApp(): void {
    if (this.canInstallIOS || this.iosPromptActive)
      this.iosPromptActive = true
    else if (this.canInstallPWA)
      this.$pwa.deferredPrompt.prompt()
  }

  async requestNotification(): Promise<void> {
    this.subscribeClicked = true

    await this.$services.accountService.requestPermission(this.$configurations.notification.publicKey, this.isiOS ? WebPlatformEnum.ios : this.isiPadOS ? WebPlatformEnum.iPadOS : WebPlatformEnum.GenericBrowser)
  }

  get notificationBannerText(): string {
    return this.$configurations.notificationBannerText
  }

  get customHomeBanner(): ICustomBanner {
    return this.$configurations.customHomeBanner
  }

  get showCustomHomeBanner(): boolean {
    if (!this.$configurations.customHomeBanner.enabled || !this.$configurations.customHomeBanner.markdown)
      return false

    if (this.$configurations.customHomeBanner.onlyShowWhen3YPFPolicyAvailable)
      return this.policies?.some(policy => policy.isEligibleFor3YPF) ?? false

    return true
  }

  handleBannerClick(): void {
    this.customHomeBanner.linkConfig?.click(this)
  }

  get canInstallPWA(): boolean {
    return this.$pwa.canInstall && this.$configurations.features.app
  }

  get browserNotification(): boolean {
    return this.$pwa.canBrowserSupport && this.$configurations.features.pushNotification
  }

  get isiOS(): boolean {
    return /iphone|ipad|ipod/.test(window.navigator.userAgent.toLowerCase())
  }

  get isiPadOS(): boolean {
    return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1
  }

  get canInstallIOS(): boolean {
    const isStandalone = 'standalone' in window.navigator && (window.navigator as any).standalone

    return (this.isiOS || this.isiPadOS) && !isStandalone && this.$configurations.features.app
  }
}
</script>
